import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type {
  Provider,
  ProviderCreate,
  ProviderUpdate,
  ProviderListResponse,
  ProviderLabel,
  PaginatedParams,
} from "@/types";

interface ProviderParams extends PaginatedParams {
  city?: string;
}

export const providersApi = {
  getAll: async (params?: ProviderParams): Promise<ProviderListResponse> => {
    const response = await apiClient.get("/providers", {
      params: toSnakeCase(params),
    });
    return (toCamelCase(response.data) as ProviderListResponse) || { items: [], total: 0 };
  },

  getById: async (id: number): Promise<Provider> => {
    const response = await apiClient.get(`/providers/${id}`);
    return toCamelCase(response.data) as Provider;
  },

  create: async (data: ProviderCreate): Promise<Provider> => {
    const response = await apiClient.post("/providers", toSnakeCase(data));
    return toCamelCase(response.data) as Provider;
  },

  update: async (id: number, data: ProviderUpdate): Promise<Provider> => {
    const response = await apiClient.put(`/providers/${id}`, toSnakeCase(data));
    return toCamelCase(response.data) as Provider;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/providers/${id}`);
  },

  // Labels
  getLabels: async (providerId: number): Promise<ProviderLabel[]> => {
    const response = await apiClient.get(`/providers/${providerId}/labels`);
    return toCamelCase(response.data) as ProviderLabel[];
  },

  addLabel: async (
    providerId: number,
    data: { name: string; description?: string }
  ): Promise<ProviderLabel> => {
    const response = await apiClient.post(
      `/providers/${providerId}/labels`,
      toSnakeCase(data)
    );
    return toCamelCase(response.data) as ProviderLabel;
  },

  deleteLabel: async (providerId: number, labelId: number): Promise<void> => {
    await apiClient.delete(`/providers/${providerId}/labels/${labelId}`);
  },

  // Vectorization
  vectorizeAll: async (): Promise<{ message: string; pending: number }> => {
    const response = await apiClient.post("/providers/vectorize-all");
    return response.data;
  },

  vectorize: async (id: number): Promise<Provider> => {
    const response = await apiClient.post(`/providers/${id}/vectorize`);
    return toCamelCase(response.data) as Provider;
  },

  // Search
  searchSemantic: async (query: string, topK: number = 5): Promise<{ query: string; results: Array<{
    provider_id: number; name: string; cif: string; city: string; labels: string; score: number; content: string;
  }> }> => {
    const response = await apiClient.get("/providers/search", { params: { q: query, top_k: topK } });
    return response.data;
  },
};
